import { appUsersApi } from '../api/modules/appUsers.api';
import { getApiErrorMessage } from '../api/client';
import { mapPaginated } from '../models/_helpers';
import { appUserFromApi, appUserKycToApi } from '../models/appUser.model';
import { downloadExcelResponse, exportFileStamp } from '../utils/exportExcel';

export async function listAppUsers(params = {}) {
  try {
    const result = await appUsersApi.list(params);
    return mapPaginated(result, appUserFromApi);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load users'));
  }
}

export async function exportAppUsersExcel(params = {}) {
  try {
    const response = await appUsersApi.exportExcel(params);
    return downloadExcelResponse(
      response,
      `members_${exportFileStamp()}.xlsx`,
    );
  } catch (error) {
    throw new Error(error?.message || getApiErrorMessage(error, 'Export failed'));
  }
}

export async function getAppUser(id) {
  try {
    return appUserFromApi(await appUsersApi.getById(id));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load user'));
  }
}

export async function updateAppUserKyc(id, body) {
  try {
    return appUserFromApi(await appUsersApi.updateKyc(id, appUserKycToApi(body)));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to update user KYC'));
  }
}

export async function sendAppUserNotification(id, body) {
  try {
    return await appUsersApi.sendNotification(id, {
      title: String(body.title || '').trim(),
      body: String(body.body || '').trim(),
    });
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to send notification'));
  }
}

export async function assignAppUserBu(id, body) {
  try {
    return appUserFromApi(
      await appUsersApi.assignBu(id, {
        buId: Number(body.buId),
        siteId: Number(body.siteId),
        isPrimary: body.isPrimary === true,
      }),
    );
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to assign business unit'));
  }
}

export async function syncAppUserBu(id, assignments) {
  try {
    return appUserFromApi(
      await appUsersApi.syncBu(id, {
        assignments: (assignments || []).map((a) => ({
          buId: Number(a.buId),
          siteId: a.siteId == null || a.siteId === '' ? null : Number(a.siteId),
        })),
      }),
    );
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to update assignments'));
  }
}

export async function revokeAppUserBu(id, buId, siteId) {
  try {
    return appUserFromApi(await appUsersApi.revokeBu(id, buId, siteId));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to revoke business unit'));
  }
}

export async function aiVerifyAppUser(id) {
  try {
    return await appUsersApi.aiVerify(id);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'AI verification failed'));
  }
}

export async function getLastAiVerifyAppUser(id) {
  try {
    return await appUsersApi.lastAiVerify(id);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load last AI verify'));
  }
}

export async function getAiVerifyHistoryAppUser(id) {
  try {
    const data = await appUsersApi.aiVerifyHistory(id);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load AI verify history'));
  }
}

export async function lookupAppUserGst(id, gstin, { forceFetch = false } = {}) {
  try {
    const body = {
      ...(gstin ? { gstin } : {}),
      forceFetch: forceFetch === true,
    };
    return await appUsersApi.gstLookup(id, body);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'GST lookup failed'));
  }
}

export async function getAppUserActivitySummary(id) {
  try {
    return await appUsersApi.activitySummary(id);
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, 'Failed to load member activity summary'),
    );
  }
}
